import { Airport, StaticData } from "./types";

type ErrorHandler<T = any> = (
  error: Error,
  message: string,
  context: T & { error?: unknown }
) => void;

/**
 * Find Airport from Area Code
 * @param areaCode string
 * @param staticData StaticData
 * @returns Airport | undefined
 */
export function findAirportsFromAreaCode(
  areaCode: string,
  staticData: StaticData,
  onError?: ErrorHandler<{ areaCode: string }>
): Airport | undefined {
  try {
    const airportCodes = staticData.areaAirportsMap[areaCode];
    if (airportCodes) {
      const [code] = airportCodes.split("|");
      return staticData.airportMap[code]!;
    }
    onError?.(
      new Error("AIRPORT_CITY_NOT_FOUND"),
      `findAirportsFromAreaCode: Can't find airport city Area Code`,
      { areaCode }
    );
  } catch (error) {
    onError?.(
      new Error("AIRPORT_CITY_ERROR"),
      `findAirportsFromAreaCode: Error finding airport city Area Code`,
      { areaCode, error }
    );
  }
}

/**
 * @param code may be Airport Code or Area Code
 */
export function guessAirportName(
  code: string,
  staticData: StaticData,
  allAiportLabel: string,
  onError?: ErrorHandler<{ code: string }>
): string {
  try {
    const airport = staticData.airportMap[code];
    if (airport) {
      return `${airport.city} (${code}) - ${airport.internationalAirportName}`;
    }

    const areaAirports = findAirportsFromAreaCode(code, staticData);
    if (areaAirports) {
      return `${areaAirports.city} (${allAiportLabel})`;
    }
    onError?.(
      new Error("AIRPORT_NAME_NOT_FOUND"),
      `guessAirportName: Can't find airport name with Airport/Area Code`,
      { code }
    );
  } catch (error) {
    onError?.(
      new Error("AIRPORT_NAME_ERROR"),
      `guessAirportName: Error guessing airport name`,
      { code, error }
    );
  }

  return code;
}

/**
 * Guess Airport City based on Airport Code or Area Code
 * @param code string
 * @param staticData StaticData
 * @returns string
 */
export function guessAirportCity(
  code: string,
  staticData: StaticData,
  onError?: ErrorHandler<{ code: string }>
): string {
  try {
    const airport = staticData.airportMap[code];
    if (airport) {
      return `${airport.city} (${code})`;
    }

    const areaAirports = findAirportsFromAreaCode(code, staticData);
    if (areaAirports) {
      return `${areaAirports.city} (${code})`;
    }
    onError?.(
      new Error("AIRPORT_CITY_NOT_FOUND"),
      `guessAirportCity: Can't find airport city with Airport/Area Code`,
      { code }
    );
  } catch (error) {
    onError?.(
      new Error("AIRPORT_CITY_ERROR"),
      `guessAirportCity: Error guessing airport city`,
      { code, error }
    );
  }

  return code;
}

export function getCityWithAirportCode(
  airportCode: string,
  staticData: StaticData,
  onError?: ErrorHandler<{ airportCode: string }>
): string {
  try {
    const airport = staticData.airportMap[airportCode];
    if (airport) {
      return `${airport.city} (${airportCode})`;
    }
    onError?.(
      new Error("AIRPORT_CITY_NOT_FOUND"),
      `getCityWithAirportCode: Can't find airport city with Airport Code`,
      { airportCode }
    );
  } catch (error) {
    onError?.(
      new Error("AIRPORT_CITY_ERROR"),
      `getCityWithAirportCode: Error getting city with Airport Code`,
      { airportCode, error }
    );
  }

  return airportCode;
}

export function getAirportNameWithAirportCode(
  airportCode: string,
  staticData: StaticData,
  onError?: ErrorHandler<{ airportCode: string }>
): string {
  try {
    const airport = staticData.airportMap[airportCode];
    if (airport) {
      return `${airport.internationalAirportName} (${airportCode})`;
    }
    onError?.(
      new Error("AIRPORT_NAME_NOT_FOUND"),
      `getAirportNameWithAirportCode: Can't find airport name with Airport Code`,
      { airportCode }
    );
  } catch (error) {
    onError?.(
      new Error("AIRPORT_NAME_ERROR"),
      `getAirportNameWithAirportCode: Error getting airport name with Airport Code`,
      { airportCode, error }
    );
  }

  return airportCode;
}

export function getAirportName(
  airportCode: string,
  staticData: StaticData,
  onError?: ErrorHandler<{ airportCode: string }>
): string {
  try {
    const airport = staticData.airportMap[airportCode];
    if (airport) {
      return airport.internationalAirportName;
    }
    onError?.(
      new Error("AIRPORT_NAME_NOT_FOUND"),
      `getAirportName: Can't find airport name with Airport Code`,
      { airportCode }
    );
  } catch (error) {
    onError?.(
      new Error("AIRPORT_NAME_ERROR"),
      `getAirportName: Error getting airport name with Airport Code`,
      { airportCode, error }
    );
  }

  return airportCode;
}

export function getAirportCity(
  airportCode: string,
  staticData: StaticData,
  onError?: ErrorHandler<{ airportCode: string }>
): string {
  try {
    const airport = staticData.airportMap[airportCode];
    if (airport) {
      return airport.city;
    }
    onError?.(
      new Error("AIRPORT_CITY_NOT_FOUND"),
      `getAirportCity: Can't find airport city with Airport Code`,
      { airportCode }
    );
  } catch (error) {
    onError?.(
      new Error("AIRPORT_CITY_ERROR"),
      `getAirportCity: Error getting airport city with Airport Code`,
      { airportCode, error }
    );
  }
  return airportCode;
}

export function guessAirline(
  flightCode: string,
  staticData: StaticData,
  onError?: ErrorHandler<{ flightCode: string }>
): string {
  try {
    const [airlineCode] = flightCode.split("-");
    const airline = staticData.airlineMap[airlineCode];
    if (airline) {
      return airline.airlineName;
    }
    onError?.(
      new Error("AIRLINE_NOT_FOUND"),
      `guessAirline: Can't find airline with flight Code`,
      { flightCode }
    );
  } catch (error) {
    onError?.(
      new Error("AIRLINE_ERROR"),
      `guessAirline: Error getting airline with flight code`,
      { flightCode, error }
    );
  }
  return flightCode;
}

export function guessLogoWithAirlineName(
  airlineName: string,
  staticData: StaticData,
  onError?: ErrorHandler<{ airlineName: string }>
): string {
  try {
    const airlineInfo = Object.values(staticData.airlineMap).find(
      (airline) => airline?.airlineName === airlineName
    );

    if (airlineInfo) {
      return airlineInfo.logoUrl;
    }
    onError?.(
      new Error("AIRLINE_NOT_FOUND"),
      `guessAirline: Can't find airline logo with airline name`,
      { airlineName }
    );
  } catch (error) {
    onError?.(
      new Error("AIRLINE_ERROR"),
      `guessAirline: Error getting airline logo with airline name`,
      { airlineName, error }
    );
  }
  return airlineName;
}
